import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import UpdateIcon from "@mui/icons-material/Update";
import RateReviewIcon from "@mui/icons-material/RateReview";

const stages = [
  {
    id: "new",
    label: "Initial Screening Telehealth Video Call",
    action: "Fill Inclusion/Exclusion Form",
    icon: PersonAddIcon,
  },
  {
    id: "approved",
    label: "Approved",
    action: "Schedule Initial Health Assessment",
    icon: VerifiedUserIcon,
  },
  {
    id: "benefit_recommendation",
    label: "First Intake Telehealth Video Call",
    action: "Review Recommended Benefits",
    icon: UpdateIcon,
  },
  {
    id: "benefit_recommendation",
    label: "Middle Intake Telehealth Video Call",
    action: "Review Recommended Benefits",
    icon: UpdateIcon,
  },
  {
    id: "benefit_assignment",
    label: "Final Intake Telehealth Video Call",
    action: "Accept Assigned Benefits",
    icon: UpdateIcon,
  },
];

const getStageStatus = (currentStage: string, stageId: string) => {
  const currentIndex = stages.findIndex((s) => s.id === currentStage);
  const stageIndex = stages.findIndex((s) => s.id === stageId);

  if (stageIndex < currentIndex) return "completed";
  if (stageIndex === currentIndex) return "current";
  return "pending";
};

interface MemberLifecycleTimelineProps {
  currentStage: string;
  onActionClick?: (stageId: string, action: string) => void;
}

const MemberLifecycleTimeline: React.FC<MemberLifecycleTimelineProps> = ({
  currentStage,
  onActionClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return theme.palette.success.main;
      case "current":
        return theme.palette.primary.main;
      case "pending":
        return theme.palette.grey[300];
    }
  };

  return (
    <Box sx={{ position: "relative", p: 2, my: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",
          pb: 2,
          position: "relative",
        }}
      >
        {/* Timeline line */}
        <Box
          sx={{
            position: "absolute",
            top: "50px",
            left: 0,
            right: 0,
            height: "2px",
            backgroundColor: theme.palette.grey[300],
            zIndex: 0,
          }}
        />

        {stages.map((stage, index) => {
          const status = getStageStatus(currentStage, stage.id);
          const StageIcon = stage.icon;
          return (
            <Box
              key={stage.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: isMobile ? "150px" : "200px",
                mx: 2,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  border: `2px solid ${getStatusColor(status)}`,
                  mb: 2,
                }}
              >
                <StageIcon
                  sx={{
                    color: getStatusColor(status),
                    fontSize: 32,
                  }}
                />
              </Paper>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: status === "current" ? "bold" : "normal",
                  textAlign: "center",
                  mb: 1,
                }}
              >
                {stage.label}
              </Typography>
              {status === "current" && (
                <Button
                  variant="contained"
                  onClick={() =>
                    onActionClick && onActionClick(stage.id, stage.action)
                  }
                  sx={{
                    mt: "auto",
                    width: "100%",
                    color: "white",
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                >
                  {stage.action}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MemberLifecycleTimeline;
